import DeleteForever from "@mui/icons-material/DeleteForever";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import DialogActions from "@mui/joy/DialogActions";
import DialogContent from "@mui/joy/DialogContent";
import DialogTitle from "@mui/joy/DialogTitle";
import Divider from "@mui/joy/Divider";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Typography from "@mui/joy/Typography";
import React from "react";
import { EventSource } from "../../../models/clinical_event";
import { ConsistencyError } from "../../../models/consistency";
import { FactId } from "../../../models/fact";
import { NoteId } from "../../../models/note";
import { useSnackbar } from "../../../shared/components/layout/SnackbarContext";
import { NoteQuartzClientContext } from "../../../shared/services/api/contexts";

interface DataConsistencyProps {
  readonly dataInconsistencies: ConsistencyError[];
  readonly onDelete: () => void;
}

const SourceItem: React.FC<{
  src: EventSource;
  onOpenNote: (noteId: NoteId) => void;
  onDeleteClick: (noteId: NoteId, factId: FactId) => void;
  isDeleting: boolean;
}> = ({ src, onOpenNote, onDeleteClick, isDeleting }) => {
  const { snippet_before, lexical_variant, snippet_after } = src.annotation[0];
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" key={src.fact_id}>
      <Typography>
        …{snippet_before}
        <strong>{` ${lexical_variant} `}</strong>
        {snippet_after}…
      </Typography>
      <Box>
        <Stack direction="row" spacing={1}>
          <Button size="sm" variant="soft" onClick={() => onOpenNote(src.note_id)}>
            Open note
          </Button>
          <Button
            size="sm"
            variant="soft"
            color="warning"
            disabled={isDeleting}
            onClick={() => onDeleteClick(src.note_id, src.fact_id)}
            aria-label="Delete fact"
          >
            Delete fact
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};

const InconsistencyRow: React.FC<{
  inc: ConsistencyError;
  index: number;
  onOpenNote: (noteId: NoteId) => void;
  onDeleteClick: (noteId: NoteId, factId: FactId) => void;
  isDeleting: boolean;
}> = ({ inc, index, onOpenNote, onDeleteClick, isDeleting }) => {
  const flatSources: EventSource[] = inc.source.flatMap((ev) => ev.source);
  return (
    <tr key={index}>
      <td>{inc.detail}</td>
      <td>
        <Stack direction="column" spacing={1}>
          {flatSources.map((src) => (
            <SourceItem
              key={src.fact_id}
              src={src}
              onOpenNote={onOpenNote}
              onDeleteClick={onDeleteClick}
              isDeleting={isDeleting}
            />
          ))}
        </Stack>
      </td>
    </tr>
  );
};

export default function DataConsistency({ dataInconsistencies, onDelete }: DataConsistencyProps) {
  const noteQuartzClient = React.useContext(NoteQuartzClientContext);
  const { showSnackbar } = useSnackbar();
  const [pending, setPending] = React.useState<{ noteId: NoteId; factId: FactId } | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);

  const openModal = (noteId: NoteId, factId: FactId) => setPending({ noteId, factId });
  const closeModal = () => {
    if (!isDeleting) setPending(null);
  };

  const confirmDelete = async () => {
    if (!pending) return;
    setIsDeleting(true);
    try {
      await noteQuartzClient.deleteFact(pending.noteId, pending.factId);
      showSnackbar({ text: "Fact deleted", color: "success" });
      onDelete();
    } catch (err: any) {
      showSnackbar({ text: err.message || "Delete failed", color: "danger" });
    } finally {
      setIsDeleting(false);
      setPending(null);
    }
  };

  const handleOpenNote = (noteId: NoteId) => window.open(`/note/${noteId}`, "_blank", "noopener");

  if (dataInconsistencies.length === 0) {
    return (
      <Sheet variant="outlined" sx={{ borderRadius: "sm", p: 2 }}>
        <Typography>No inconsistencies found</Typography>
      </Sheet>
    );
  }

  return (
    <>
      <Sheet variant="outlined" sx={{ borderRadius: "sm" }}>
        <Table aria-label="inconsistencies table">
          <thead>
            <tr>
              <th style={{ width: "30%" }}>Details</th>
              <th>Sources</th>
            </tr>
          </thead>
          <tbody>
            {dataInconsistencies.map((inc, i) => (
              <InconsistencyRow
                key={`inc-${i}-${inc.detail.slice(0, 20)}`}
                inc={inc}
                index={i}
                onOpenNote={handleOpenNote}
                onDeleteClick={openModal}
                isDeleting={isDeleting}
              />
            ))}
          </tbody>
        </Table>
      </Sheet>

      <Modal open={!!pending} onClose={closeModal} disableEscapeKeyDown={isDeleting} disablePortal>
        <ModalDialog variant="outlined" role="alertdialog" aria-labelledby="confirm-dialog-title">
          <DialogTitle id="confirm-dialog-title">
            <WarningRoundedIcon color="warning" sx={{ mr: 1, verticalAlign: "middle" }} />
            Delete this fact?
          </DialogTitle>
          <Divider />
          <DialogContent>
            <Typography>
              Are you sure you want to permanently delete this fact? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="plain" color="neutral" onClick={closeModal} disabled={isDeleting}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="danger"
              startDecorator={<DeleteForever />}
              onClick={confirmDelete}
              loading={isDeleting}
            >
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
}
